// apps/web/src/pages/AudienceMapPage.tsx
//
// Fan economics geography map — streaming audience, fan demand, merch revenue,
// and Chartmetric past-performance venue overlays.

import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import { feature as topoFeature } from 'topojson-client'
import type { Topology, GeometryCollection } from 'topojson-specification'
import type { Feature, FeatureCollection, Geometry } from 'geojson'
import { Map as MapIcon, Globe, Activity, PoundSterling, MapPin } from 'lucide-react'
import { ApiClientError } from '@/lib/api-client'
import { artistFetch } from '@/lib/artist-api'
import { PageHeader } from '@/components/PageHeader'
import { normaliseCountryName } from '@/lib/world-atlas-aliases'

const WORLD_ATLAS_URL = 'https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json'

interface CountryStat {
  countryCode: string
  count?: number | null
  revenuePence?: number | null
  pct?: number | null
}
interface CityStat {
  city: string
  countryCode?: string | null
  count?: number | null
  revenuePence?: number | null
  pct?: number | null
  lat?: number | null
  lng?: number | null
}

interface PlatformLayer {
  platform: string
  label: string
  snapshotDate: string | null
  totalReach: number | null
  totalRevenuePence: number | null
  topCountries: CountryStat[]
  topCities: CityStat[]
}

interface VenueMapPin {
  id: string
  kind: 'artist' | 'peer'
  name: string
  city: string | null
  countryCode: string | null
  lat: number
  lng: number
  peerName: string | null
  capacity: number | null
  eventName: string | null
  eventDate: string | null
}

interface FanEconomicsGeography {
  layers: PlatformLayer[]
  fanDemand: PlatformLayer | null
  merchStore: PlatformLayer | null
  unified: PlatformLayer
  venueOverlay: {
    artistVenues: VenueMapPin[]
    peerVenues: VenueMapPin[]
    fetchedAt: string | null
  }
  summary: {
    connectedPlatforms: number
    totalMerchRevenuePence: number
    totalMerchOrders: number
    fanDemandCities: number
    chartmetricVenueCount: number
    peerVenueCount: number
  }
}

interface CountryFeatureProps {
  name: string
}

const PLATFORM_COLOR: Record<string, string> = {
  unified: '#0f172a',
  merch_store: '#ea580c',
  fan_demand: '#2563eb',
  chartmetric: '#7c3aed',
  spotify: '#1db954',
  meta: '#1877f2',
  instagram: '#e4405f',
  youtube: '#ff0000',
  tiktok: '#010101',
  ga4: '#f9ab00',
  search_console: '#4285f4',
  hivecells: '#fbbf24',
}

const VENUE_COLOR = {
  artist: '#7c3aed',
  peer: '#ea580c',
}

const COUNTRY_CENTROIDS: Record<string, [number, number]> = {
  US: [39.5, -98.35], GB: [54.0, -2.0], DE: [51.0, 9.0], FR: [46.0, 2.0],
  IT: [42.0, 12.5], ES: [40.0, -4.0], NL: [52.1, 5.3], CA: [56.0, -106.0],
  AU: [-25.0, 134.0], MX: [23.6, -102.5], BR: [-10.0, -55.0], JP: [36.0, 138.0],
  KR: [36.5, 127.8], IN: [20.6, 78.9], PL: [52.0, 19.0], SE: [62.0, 15.0],
  NO: [62.0, 10.0], DK: [56.0, 10.0], FI: [64.0, 26.0], BE: [50.5, 4.5],
  AT: [47.5, 14.5], CH: [46.8, 8.2], IE: [53.4, -8.0], PT: [39.4, -8.2],
  ZA: [-30.5, 22.9], NZ: [-41.0, 174.0],
}

function formatMoney(pence: number): string {
  return `£${(pence / 100).toFixed(0)}`
}

function flattenLayers(data: FanEconomicsGeography | undefined): PlatformLayer[] {
  if (!data) return []
  const extra = [data.unified, data.merchStore, data.fanDemand].filter(Boolean) as PlatformLayer[]
  const platforms = new Set(extra.map((l) => l.platform))
  const streaming = data.layers.filter((l) => !platforms.has(l.platform))
  return [...extra, ...streaming]
}

function layerUsesRevenue(layer: PlatformLayer | null): boolean {
  if (!layer) return false
  return layer.platform === 'merch_store' || layer.platform === 'unified'
}

export function AudienceMapPage() {
  const geoQ = useQuery<FanEconomicsGeography, ApiClientError>({
    queryKey: ['audience-geography'],
    queryFn: () => artistFetch<FanEconomicsGeography>('/audience/geography'),
  })

  const allLayers = useMemo(() => flattenLayers(geoQ.data), [geoQ.data])
  const usableLayers = useMemo(
    () => allLayers.filter((l) => l.topCountries.length > 0 || l.topCities.length > 0),
    [allLayers],
  )
  const [activePlatform, setActivePlatform] = useState<string | null>(null)
  const [showArtistVenues, setShowArtistVenues] = useState(true)
  const [showPeerVenues, setShowPeerVenues] = useState(false)

  useEffect(() => {
    if (!activePlatform && usableLayers.length > 0 && usableLayers[0]) {
      const preferred = usableLayers.find((l) => l.platform === 'unified') ?? usableLayers[0]
      setActivePlatform(preferred.platform)
    }
  }, [usableLayers, activePlatform])

  const activeLayer = usableLayers.find((l) => l.platform === activePlatform) ?? null
  const summary = geoQ.data?.summary
  const venueOverlay = geoQ.data?.venueOverlay
  const hasVenueData =
    (venueOverlay?.artistVenues.length ?? 0) > 0 || (venueOverlay?.peerVenues.length ?? 0) > 0

  return (
    <div className="space-y-4">
      <PageHeader
        title="Fan economics map"
        subtitle="Streaming audience, fan demand, merch revenue, and Chartmetric past-show overlays."
      />

      {summary && (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
          <SummaryCard label="Data layers" value={String(summary.connectedPlatforms)} />
          <SummaryCard
            label="Merch revenue"
            value={formatMoney(summary.totalMerchRevenuePence)}
            icon={<PoundSterling className="h-4 w-4" />}
          />
          <SummaryCard label="Store orders" value={summary.totalMerchOrders.toLocaleString()} />
          <SummaryCard label="Fan demand cities" value={summary.fanDemandCities.toLocaleString()} />
          <SummaryCard
            label="CM venues on map"
            value={`${summary.chartmetricVenueCount} you · ${summary.peerVenueCount} peers`}
            icon={<MapPin className="h-4 w-4" />}
          />
        </div>
      )}

      {geoQ.isLoading && <p className="text-sm text-zinc-500">Loading map data…</p>}
      {geoQ.isError && (
        <p className="text-sm text-red-600">
          Could not load geography: {geoQ.error.message}
        </p>
      )}

      {!geoQ.isLoading && usableLayers.length === 0 && !hasVenueData && <EmptyState />}

      {(usableLayers.length > 0 || hasVenueData) && (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-[18rem_1fr]">
          <LayerPanel
            layers={usableLayers}
            activePlatform={activePlatform}
            onSelect={setActivePlatform}
            showArtistVenues={showArtistVenues}
            showPeerVenues={showPeerVenues}
            onToggleArtistVenues={() => setShowArtistVenues((v) => !v)}
            onTogglePeerVenues={() => setShowPeerVenues((v) => !v)}
            artistVenueCount={venueOverlay?.artistVenues.length ?? 0}
            peerVenueCount={venueOverlay?.peerVenues.length ?? 0}
            venueFetchedAt={venueOverlay?.fetchedAt}
          />
          <MapPanel
            layer={activeLayer}
            venueOverlay={venueOverlay}
            showArtistVenues={showArtistVenues}
            showPeerVenues={showPeerVenues}
          />
        </div>
      )}
    </div>
  )
}

function SummaryCard({
  label,
  value,
  icon,
}: {
  label: string
  value: string
  icon?: ReactNode
}) {
  return (
    <div className="rounded-lg border border-zinc-200 bg-white px-4 py-3">
      <p className="flex items-center gap-1 text-xs font-medium text-zinc-500">
        {icon}
        {label}
      </p>
      <p className="mt-1 text-lg font-semibold text-zinc-900">{value}</p>
    </div>
  )
}

function LayerPanel({
  layers,
  activePlatform,
  onSelect,
  showArtistVenues,
  showPeerVenues,
  onToggleArtistVenues,
  onTogglePeerVenues,
  artistVenueCount,
  peerVenueCount,
  venueFetchedAt,
}: {
  layers: PlatformLayer[]
  activePlatform: string | null
  onSelect: (p: string) => void
  showArtistVenues: boolean
  showPeerVenues: boolean
  onToggleArtistVenues: () => void
  onTogglePeerVenues: () => void
  artistVenueCount: number
  peerVenueCount: number
  venueFetchedAt: string | null | undefined
}) {
  return (
    <aside className="rounded-xl border border-zinc-200 bg-white p-4">
      <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold text-zinc-900">
        <Globe className="h-4 w-4" /> Layers
      </h2>
      <ul className="space-y-1">
        {layers.map((l) => {
          const active = l.platform === activePlatform
          const revenue = l.totalRevenuePence
          return (
            <li key={l.platform}>
              <button
                type="button"
                onClick={() => onSelect(l.platform)}
                className={`flex w-full items-start gap-2 rounded px-2 py-1.5 text-left text-xs transition ${
                  active ? 'bg-zinc-900 text-white' : 'hover:bg-zinc-100 text-zinc-900'
                }`}
              >
                <span
                  className="mt-0.5 inline-block h-3 w-3 shrink-0 rounded-full"
                  style={{ background: PLATFORM_COLOR[l.platform] ?? '#999' }}
                />
                <span className="flex-1">
                  <span className="block font-medium">{l.label}</span>
                  <span className={`block ${active ? 'text-zinc-300' : 'text-zinc-500'}`}>
                    {l.topCountries.length} countries · {l.topCities.length} cities
                  </span>
                  {revenue != null && revenue > 0 && (
                    <span className={`block ${active ? 'text-zinc-300' : 'text-zinc-500'}`}>
                      {formatMoney(revenue)} revenue
                    </span>
                  )}
                  {l.snapshotDate && (
                    <span className={`block ${active ? 'text-zinc-400' : 'text-zinc-400'}`}>
                      Updated {new Date(l.snapshotDate).toLocaleDateString()}
                    </span>
                  )}
                </span>
              </button>
            </li>
          )
        })}
      </ul>

      <div className="mt-4 border-t border-zinc-100 pt-4">
        <h3 className="mb-2 flex items-center gap-1 text-xs font-semibold text-zinc-900">
          <MapPin className="h-3.5 w-3.5" /> Performance overlays
        </h3>
        <label className="flex items-center gap-2 py-1 text-xs text-zinc-700">
          <input
            type="checkbox"
            checked={showArtistVenues}
            onChange={onToggleArtistVenues}
            disabled={artistVenueCount === 0}
          />
          <span
            className="inline-block h-2.5 w-2.5 rounded-sm"
            style={{ background: VENUE_COLOR.artist }}
          />
          Your past shows ({artistVenueCount})
        </label>
        <label className="flex items-center gap-2 py-1 text-xs text-zinc-700">
          <input
            type="checkbox"
            checked={showPeerVenues}
            onChange={onTogglePeerVenues}
            disabled={peerVenueCount === 0}
          />
          <span
            className="inline-block h-2.5 w-2.5 rounded-sm"
            style={{ background: VENUE_COLOR.peer }}
          />
          Similar artist shows ({peerVenueCount})
        </label>
        {venueFetchedAt && (
          <p className="mt-2 text-[10px] text-zinc-500">
            Venues from Chartmetric · {new Date(venueFetchedAt).toLocaleDateString()}
          </p>
        )}
        {artistVenueCount === 0 && peerVenueCount === 0 && (
          <p className="mt-2 text-[10px] text-zinc-500">
            Connect Chartmetric and run a weekly sync, or refresh similar artists on{' '}
            <Link to="/audience" className="underline">Audience</Link>.
          </p>
        )}
      </div>

      <p className="mt-4 text-[10px] leading-relaxed text-zinc-500">
        <strong>Unified</strong> blends streaming, fan demand, and merch. Purple squares =
        your Chartmetric venues; orange = similar artists&apos; recent cities.
      </p>
    </aside>
  )
}

function MapPanel({
  layer,
  venueOverlay,
  showArtistVenues,
  showPeerVenues,
}: {
  layer: PlatformLayer | null
  venueOverlay: FanEconomicsGeography['venueOverlay'] | undefined
  showArtistVenues: boolean
  showPeerVenues: boolean
}) {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const mapRef = useRef<L.Map | null>(null)
  const choroplethRef = useRef<L.GeoJSON | null>(null)
  const bubblesRef = useRef<L.LayerGroup | null>(null)
  const venuesRef = useRef<L.LayerGroup | null>(null)
  const [topology, setTopology] = useState<FeatureCollection<Geometry, CountryFeatureProps> | null>(null)
  const [topologyError, setTopologyError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    void (async () => {
      try {
        const res = await fetch(WORLD_ATLAS_URL)
        if (!res.ok) throw new Error(`world-atlas ${res.status}`)
        const topo = (await res.json()) as Topology
        const collection = topo.objects.countries as GeometryCollection
        const fc = topoFeature(topo, collection) as FeatureCollection<Geometry, CountryFeatureProps>
        if (!cancelled) setTopology(fc)
      } catch (err) {
        if (!cancelled) setTopologyError(err instanceof Error ? err.message : 'Unknown')
      }
    })()
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return
    const map = L.map(containerRef.current, {
      worldCopyJump: true,
      minZoom: 1,
      maxBounds: [
        [-85, -180],
        [85, 180],
      ],
    }).setView([25, 0], 2)
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap contributors',
      maxZoom: 8,
    }).addTo(map)
    bubblesRef.current = L.layerGroup().addTo(map)
    venuesRef.current = L.layerGroup().addTo(map)
    mapRef.current = map
    return () => {
      map.remove()
      mapRef.current = null
    }
  }, [])

  useEffect(() => {
    const map = mapRef.current
    if (!map || !topology) return

    if (choroplethRef.current) {
      choroplethRef.current.remove()
      choroplethRef.current = null
    }
    bubblesRef.current?.clearLayers()
    venuesRef.current?.clearLayers()

    if (layer) {
      const useRevenue = layerUsesRevenue(layer)
      const countryValue = (c: CountryStat) =>
        useRevenue ? (c.revenuePence ?? 0) : (c.count ?? 0)
      const maxCountry = Math.max(
        1,
        ...layer.topCountries.map((c) => countryValue(c)),
      )
      const color = PLATFORM_COLOR[layer.platform] ?? '#7c3aed'

      const lookupByName = (name: string): CountryStat | undefined => {
        const normalised = normaliseCountryName(name)
        for (const c of layer.topCountries) {
          if (c.countryCode && normalised.toLowerCase() === isoToName(c.countryCode).toLowerCase()) {
            return c
          }
        }
        return undefined
      }

      choroplethRef.current = L.geoJSON(topology, {
        style: (f) => {
          const feat = f as Feature<Geometry, CountryFeatureProps>
          const stat = lookupByName(feat.properties.name)
          const val = stat ? countryValue(stat) : 0
          if (!stat || val === 0) {
            return { fillColor: '#e5e7eb', fillOpacity: 0.15, color: '#d4d4d8', weight: 0.5 }
          }
          const intensity = Math.min(1, val / maxCountry)
          return {
            fillColor: color,
            fillOpacity: 0.2 + 0.65 * intensity,
            color: '#52525b',
            weight: 0.6,
          }
        },
        onEachFeature: (f, lyr) => {
          const feat = f as Feature<Geometry, CountryFeatureProps>
          const stat = lookupByName(feat.properties.name)
          const reach = stat?.count?.toLocaleString() ?? 'no data'
          const revenue =
            stat?.revenuePence != null && stat.revenuePence > 0
              ? formatMoney(stat.revenuePence)
              : ''
          const pct = stat?.pct != null ? `${(stat.pct * 100).toFixed(1)}%` : ''
          lyr.bindTooltip(
            `<strong>${feat.properties.name}</strong><br/>${reach}${revenue ? ` · ${revenue}` : ''}${pct ? ` (${pct})` : ''}`,
            { sticky: true },
          )
        },
      }).addTo(map)

      const cityValue = (c: CityStat) =>
        useRevenue ? (c.revenuePence ?? 0) : (c.count ?? 0)
      const maxCity = Math.max(1, ...layer.topCities.map((c) => cityValue(c)))

      for (const city of layer.topCities) {
        const fallback = city.countryCode
          ? COUNTRY_CENTROIDS[city.countryCode.toUpperCase()]
          : undefined
        const finalLat = city.lat ?? fallback?.[0]
        const finalLng = city.lng ?? fallback?.[1]
        if (finalLat == null || finalLng == null) continue
        const intensity = Math.min(1, cityValue(city) / maxCity)
        const radius = 4 + 12 * intensity
        const revenueLabel =
          city.revenuePence != null && city.revenuePence > 0
            ? ` · ${formatMoney(city.revenuePence)}`
            : ''
        L.circleMarker([finalLat, finalLng], {
          radius,
          color,
          weight: 1,
          fillColor: color,
          fillOpacity: 0.6,
        })
          .bindTooltip(
            `<strong>${city.city}${city.countryCode ? `, ${city.countryCode}` : ''}</strong><br/>${(city.count ?? 0).toLocaleString()}${revenueLabel}`,
          )
          .addTo(bubblesRef.current!)
      }
    } else {
      choroplethRef.current = L.geoJSON(topology, {
        style: {
          fillColor: '#e5e7eb',
          fillOpacity: 0.15,
          color: '#d4d4d8',
          weight: 0.5,
        },
      }).addTo(map)
    }

    const drawVenue = (pin: VenueMapPin, color: string) => {
      const size = pin.kind === 'artist' ? 10 : 8
      const icon = L.divIcon({
        className: '',
        html: `<div style="width:${size}px;height:${size}px;background:${color};border:1px solid #fff;box-shadow:0 1px 3px rgba(0,0,0,.35)"></div>`,
        iconSize: [size, size],
        iconAnchor: [size / 2, size / 2],
      })
      const loc =
        pin.city && pin.countryCode
          ? `${pin.city}, ${pin.countryCode}`
          : pin.city ?? pin.countryCode ?? ''
      const peer = pin.peerName ? `<br/><em>Similar: ${pin.peerName}</em>` : ''
      const event = pin.eventName
        ? `<br/>${pin.eventName}${pin.eventDate ? ` (${pin.eventDate})` : ''}`
        : ''
      const cap = pin.capacity ? `<br/>Cap ~${pin.capacity.toLocaleString()}` : ''
      L.marker([pin.lat, pin.lng], { icon })
        .bindTooltip(`<strong>${pin.name}</strong>${loc ? `<br/>${loc}` : ''}${peer}${event}${cap}`)
        .addTo(venuesRef.current!)
    }

    if (showArtistVenues && venueOverlay) {
      for (const pin of venueOverlay.artistVenues) {
        drawVenue(pin, VENUE_COLOR.artist)
      }
    }
    if (showPeerVenues && venueOverlay) {
      for (const pin of venueOverlay.peerVenues) {
        drawVenue(pin, VENUE_COLOR.peer)
      }
    }
  }, [layer, topology, venueOverlay, showArtistVenues, showPeerVenues])

  return (
    <div className="rounded-xl border border-zinc-200 bg-white">
      <header className="flex items-center justify-between border-b border-zinc-200 px-4 py-2">
        <h2 className="flex items-center gap-2 text-sm font-semibold text-zinc-900">
          <MapIcon className="h-4 w-4" />
          {layer?.label ?? 'Venue overlay'}
        </h2>
        {layer && (
          <span className="text-xs text-zinc-500">
            {layer.totalReach != null && `Reach ${layer.totalReach.toLocaleString()}`}
            {layer.totalRevenuePence != null && layer.totalRevenuePence > 0 && (
              <> · {formatMoney(layer.totalRevenuePence)} merch</>
            )}
          </span>
        )}
      </header>
      {topologyError && (
        <p className="px-4 py-6 text-xs text-red-600">
          Could not load world map: {topologyError}
        </p>
      )}
      <div ref={containerRef} style={{ height: '70vh', minHeight: 400 }} />
    </div>
  )
}

function EmptyState() {
  return (
    <div className="rounded-xl border border-dashed border-zinc-300 bg-white p-10 text-center">
      <Activity className="mx-auto h-10 w-10 text-zinc-400" />
      <h2 className="mt-3 text-sm font-semibold text-zinc-900">No fan economics data yet</h2>
      <p className="mt-1 text-xs text-zinc-600">
        Connect platforms on the{' '}
        <Link to="/audience" className="underline">Audience</Link> page, link{' '}
        <Link to="/integrations" className="underline">Shopify or WooCommerce</Link>, and sync
        Velvet Rope fan demand. Chartmetric weekly sync adds past-performance venue pins.
      </p>
    </div>
  )
}

const ISO2_TO_NAME: Record<string, string> = {
  US: 'United States', GB: 'United Kingdom', DE: 'Germany', FR: 'France',
  IT: 'Italy', ES: 'Spain', NL: 'Netherlands', CA: 'Canada', AU: 'Australia',
  MX: 'Mexico', BR: 'Brazil', JP: 'Japan', KR: 'South Korea', IN: 'India',
  PL: 'Poland', SE: 'Sweden', NO: 'Norway', DK: 'Denmark', FI: 'Finland',
  BE: 'Belgium', AT: 'Austria', CH: 'Switzerland', IE: 'Ireland', PT: 'Portugal',
  ZA: 'South Africa', NZ: 'New Zealand', AR: 'Argentina', CL: 'Chile',
  CO: 'Colombia', PE: 'Peru', VE: 'Venezuela', UA: 'Ukraine', RU: 'Russia',
  TR: 'Turkey', GR: 'Greece', RO: 'Romania', CZ: 'Czechia', HU: 'Hungary',
  IL: 'Israel', AE: 'United Arab Emirates', SA: 'Saudi Arabia',
  EG: 'Egypt', NG: 'Nigeria', KE: 'Kenya', GH: 'Ghana',
}

function isoToName(iso: string): string {
  return ISO2_TO_NAME[iso.toUpperCase()] ?? iso
}
